import { Section } from "../../../components/Section";
import { CurrencyInput } from "../../../components/CurrencyInput";
import { FormField, NumberInput, Select } from "../../../components/FormField";
import { IncomeCalculationsDebug } from "../../../components/IncomeCalculationsDebug";
import { InfoIcon } from "../../../components/ui/Tooltip";
import { IncomeDetails } from "../../../types/mortgage";
import { formatCurrency } from "../../../utils/utils";

interface IncomeSectionProps {
  data: IncomeDetails;
  onChange: (data: IncomeDetails) => void;
  mortgageCalculations: any;
  showValidation: boolean;
  isAdmin?: boolean;
}

interface CurrencyInputWithInfoProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  placeholder: string;
  currencySymbol?: string;
  tooltip: string;
  error?: string;
  required?: boolean;
}

function CurrencyInputWithInfo({ label, value, onChange, placeholder, currencySymbol, tooltip, error, required }: CurrencyInputWithInfoProps) {
  return (
    <div className="space-y-1">
      <div className="flex items-center">
        <label className="block text-sm font-subheader text-gray-700 dark:text-gray-300">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <InfoIcon tooltip={tooltip} />
      </div>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
            {currencySymbol || "£"}
          </span>
        </div>
        <input
          type="text"
          value={value === 0 ? "" : (value / 100).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
          onChange={(e) => {
            const numericValue = e.target.value.replace(/[^\d.]/g, "");
            const parsedValue = parseFloat(numericValue) || 0;
            onChange(Math.round(parsedValue * 100));
          }}
          placeholder={placeholder}
          className={`block w-full pl-8 pr-3 py-2 border rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none dark:bg-gray-700 dark:text-gray-100 sm:text-sm ${
            error 
              ? "border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-500" 
              : "border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500"
          }`}
        />
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}

export function IncomeSection({ data, onChange, mortgageCalculations, showValidation, isAdmin = false }: IncomeSectionProps) {
  const updateField = <K extends keyof IncomeDetails>(field: K, value: IncomeDetails[K]) => {
    onChange({ ...data, [field]: value });
  };

  // Validation: either UK income or foreign income (converted to sterling) is required
  const hasAnyIncome =
    data.totalIncomeNetOfTax > 0 ||
    (data.foreignIncome > 0 && data.foreignCurrency !== "None");
  const totalIncomeError =
    showValidation && !hasAnyIncome
      ? "Enter your total UK income net of tax, or if your income is solely from abroad, enter it as Foreign Income (converted to sterling) in the Non-UK Income section below."
      : undefined;

  return (
    <Section title="Income Details" colorTheme={1}>
      {/* UK Income Section */}
      <div className="mb-8">
        <h4 className="text-lg font-subheader text-gray-900 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
          UK Income
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Total Income Net of Tax */}
        <CurrencyInputWithInfo
          label="Total annual income net of tax"
          value={data.totalIncomeNetOfTax}
          onChange={(value) => updateField("totalIncomeNetOfTax", value)}
          placeholder="0.00"
          tooltip="Enter the total annual income after tax. This should include salary, bonuses, and any other regular income sources, but exclude income from properties which is entered separately. For borrowers with significant proportions of income linked to bonuses or investments, you should expect this calculator to be less accurate."
          error={totalIncomeError}
        />

        {/* Income from Properties */}
        <CurrencyInputWithInfo
          label="Income from Properties"
          value={data.incomeFromProperties}
          onChange={(value) => updateField("incomeFromProperties", value)}
          placeholder="0.00"
          tooltip="All income from Buy To Let and other investment properties, net of tax."
        />

        </div>
      </div>

      {/* Non-UK Income Section */}
      <div className="mb-6">
        <h4 className="text-lg font-subheader text-gray-900 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
          Non-UK Income
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Original currency">
            <Select
              value={data.foreignCurrency}
              onChange={(value) => updateField("foreignCurrency", value)}
              options={["None", "US$", "Euro", "Other"]}
            />
          </FormField>

          <CurrencyInputWithInfo
            label="Foreign Income (converted to GBP)"
            value={data.foreignIncome}
            onChange={(value) => updateField("foreignIncome", value)}
            placeholder="0.00"
            currencySymbol="£"
            tooltip="Enter the total annual income net of tax, converted into GBP. Our calculator will apply the relevant haircut to the income. Where there are multiple currencies, select the one with most income, or ‘Other’."
          />
        </div>
      </div>

      {/* Debug Information */}
      {isAdmin && mortgageCalculations && (
        <IncomeCalculationsDebug mortgageCalculations={mortgageCalculations} />
      )}
    </Section>
  );
}
